<template>
	<div class="min-h-screen w-full bg-gradient-to-br from-[#002F42] via-[#013a52] to-[#00131c] flex flex-col">
    <div class="mm-join__center">
      <div class="mm-join__col" v-if="loading == false">
        <div class="mm-card">
          <!-- Branded header -->
          <div class="mm-card__head">
            <img
              v-if="logoSrc"
              :src="logoSrc"
              alt="Mentor Mentee"
              class="mm-card__logo"
            />
            <div v-else class="mm-card__wordmark">MENTOR MENTEE</div>
            <template v-if="step !== 'done' && (invite.locationName)" >
              <p class="mm-card__eyebrow">You're joining</p>
              <p class="mm-card__location">{{ invite.locationName }}</p>
              <p v-if="invite.pathwayName" class="mm-card__pathway">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-route w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:240:22" data-component-name="RouteIcon"><circle cx="6" cy="19" r="3"></circle><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"></path><circle cx="18" cy="5" r="3"></circle></svg>
                {{ invite.pathwayName }}{{ invite.stepName ? " · " + invite.stepName : "" }}
              </p>
            </template>
          </div>

          <!-- Progress -->
          <div v-if="step !== 'done'" class="mm-card__progress" style="background-color: #FFF; padding-bottom: 20px;">
            <div
              v-for="(s, i) in progressSteps"
              :key="s"
              class="mm-bar"
              :class="{ 'mm-bar--on': i <= stepIndex }"
            />
          </div>
					<div  style="background-color: #FFF;">
					<b-alert
						:show="dismissCountDown"
						dismissible
						variant="success"
						@dismissed="dismissCountDown=0"
						@dismiss-count-down="countDownChanged"
						>
						{{ success_message }}
					</b-alert>
					<b-alert
						:show="dismissCountWarningDown"
						dismissible
						variant="danger"
						@dismissed="dismissCountWarningDown=0"
						@dismiss-count-down="countDownWarningChanged"
						>
						{{ warning_message }}
					</b-alert>
          </div>

          <div class="mm-card__body" v-if="(invite.locationName)" style="background-color: #FFF;">
            <!-- STEP 1 — invitation password -->
            <form v-if="step === 'password'" class="mm-stack" @submit.prevent="submitInvitePassword">
              <div class="mm-center">
                <div class="mm-badge"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-key-round w-6 h-6 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:257:22" data-component-name="KeyRound"><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle></svg></div>
                <h1 class="mm-h1">Welcome! Enter your invitation password</h1>
                <p class="mm-sub">
                  Your mentor or admin shared a password with you. Enter it below
                  to get started.
                </p>
              </div>

              <div class="mm-field">
                <label for="invite-password" class="mm-label">Invitation Password</label>
                <div class="mm-row">
                  <input
                    id="invite-password"
                    ref="invitePw"
                    class="mm-input mm-mono"
                    :class="{ 'mm-input--error': pwError }"
                    :type="showInvitePw ? 'text' : 'password'"
                    v-model="invitePw"
                    placeholder="Enter password"
                    @input="pwError = false"
                  />
                  <button
                    type="button"
                    class="mm-icon-btn"
                    :aria-label="showInvitePw ? 'Hide password' : 'Show password'"
                    @click="showInvitePw = !showInvitePw"
                  >
										<svg v-if="showInvitePw" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:22" data-component-name="EyeOff"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path><path d="m2 2 20 20"></path></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:55" data-component-name="Eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  </button>
                </div>
                <p v-if="showHints" class="mm-hint">
                  Prototype tip: this invitation's password is
                  <code class="mm-code">{{ demoPassword }}</code>.
                </p>
              </div>

              <button type="submit" class="mm-btn mm-btn--orange mm-btn--full">
                <span>Continue</span> <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4 ml-2" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:305:20" data-component-name="ArrowRight"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </button>
            </form>

            <!-- STEP 2 — account creation -->
            <form v-else-if="step === 'account'" class="mm-stack" @submit.prevent="submitAccount">
              <div class="mm-center">
                <div class="mm-badge"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user w-6 h-6 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:315:22" data-component-name="User"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg></div>
                <h1 class="mm-h1">Create your account</h1>
                <p class="mm-sub">
                  Tell us who you are. This is how you'll appear to your mentor
                  and team.
                </p>
              </div>

              <div class="mm-grid2">
                <div class="mm-field">
                  <label for="first-name" class="mm-label">First Name</label>
                  <input
                    id="first-name"
                    class="mm-input"
                    autocomplete="given-name"
                    v-model="firstName"
                    placeholder="Jane"
                  />
                </div>
                <div class="mm-field">
                  <label for="last-name" class="mm-label">Last Name</label>
                  <input
                    id="last-name"
                    class="mm-input"
                    autocomplete="family-name"
                    v-model="lastName"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div class="mm-field">
                <label for="email" class="mm-label">Email</label>
                <input
                  id="email"
                  class="mm-input"
                  type="email"
                  autocomplete="email"
                  v-model="email"
                  placeholder="jane@example.com"
                />
                <p v-if="email.length > 0 && !emailValid" class="mm-error-text">
                  Enter a valid email address.
                </p>
              </div>

              <div class="mm-row">
                <button type="button" class="mm-btn mm-btn--outline" @click="step = 'password'">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left w-4 h-4 mr-2" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:373:22" data-component-name="ArrowLeft"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg> <span>Back</span>
                </button>
                <button
                  type="submit"
                  class="mm-btn mm-btn--orange mm-btn--grow"
                  :disabled="!accountComplete || submitting"
                >
                  <Loader2 v-if="submitting" :size="16" class="mm-spin" />
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail w-4 h-4 mr-2" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:384:24" data-component-name="Mail"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg>
                  <span>Send verification code</span>
                </button>
              </div>
            </form>

            <!-- STEP 3 — email verification -->
            <div v-else-if="step === 'verify'" class="mm-stack">
              <div class="mm-center">
                <div class="mm-badge"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail w-6 h-6 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:397:22" data-component-name="Mail"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg></div>
                <h1 class="mm-h1">Verify your email</h1>
                <p class="mm-sub">
                  We sent a 6-digit code to
                  <span class="mm-strong">{{ email.trim() }}</span>. Enter it below.
                </p>
              </div>

              <div class="mm-otp-wrap">
                <OtpInput
                  :value="code"
                  :length="6"
                  :invalid="codeError"
                  :autofocus="autofocus"
                  @input="onCodeInput"
									ref="otpInput"
                  @complete="submitCode"
                />
                <p v-if="codeError" class="mm-error-text">
                  That code isn't right. Try again.
                </p>
                <p v-if="showHints" class="mm-hint">
                  Demo code: <code class="mm-code">{{ demoCode }}</code>
                </p>
              </div>

              <button type="button" class="mm-link" @click="resendCode">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-refresh-cw w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:448:20" data-component-name="RefreshCw"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></svg> Resend code
              </button>

              <div class="mm-row">
                <button type="button" class="mm-btn mm-btn--outline" @click="step = 'account'">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left w-4 h-4 mr-2" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:458:22" data-component-name="ArrowLeft"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg> <span>Back</span>
                </button>
                <button
                  type="button"
                  class="mm-btn mm-btn--orange mm-btn--grow"
                  :disabled="code.length !== 6"
                  @click="submitCode()"
                >
                  <span>Verify</span> <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4 ml-2" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:468:22" data-component-name="ArrowRight"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                </button>
              </div>
            </div>

            <!-- STEP 4 — set password -->
            <form v-else-if="step === 'setPassword'" class="mm-stack" @submit.prevent="submitPassword">
              <div class="mm-center">
                <div class="mm-badge"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shield-check w-6 h-6 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:479:22" data-component-name="ShieldCheck"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m9 12 2 2 4-4"></path></svg></div>
                <h1 class="mm-h1">Set your password</h1>
                <p class="mm-sub">
                  Choose a password to secure your account. Special characters
                  are welcome.
                </p>
              </div>

              <!-- Hidden username helps password managers (Apple/Google) save the login -->
              <input
                type="text"
                name="username"
                autocomplete="username"
                :value="email.trim()"
                readonly
                hidden
              />

              <div class="mm-field">
                <label for="new-password" class="mm-label">Password</label>
                <div class="mm-row">
                  <input
                    id="new-password"
                    class="mm-input"
                    :type="showNewPw ? 'text' : 'password'"
                    autocomplete="new-password"
                    v-model="newPw"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    class="mm-icon-btn"
                    :aria-label="showNewPw ? 'Hide password' : 'Show password'"
                    @click="showNewPw = !showNewPw"
                  >
										<svg v-if="showNewPw" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:22" data-component-name="EyeOff"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path><path d="m2 2 20 20"></path></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:55" data-component-name="Eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  </button>
                </div>
              </div>

              <div class="mm-field">
                <label for="confirm-password" class="mm-label">Confirm Password</label>
                <input
                  id="confirm-password"
                  class="mm-input"
                  :type="showNewPw ? 'text' : 'password'"
                  autocomplete="new-password"
                  v-model="confirmPw"
                  placeholder="Re-enter your password"
                />
              </div>

              <div class="mm-checks">
                <p class="mm-check" :class="{ 'mm-check--ok': newPwValid }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:543:22" data-component-name="Check"><path d="M20 6 9 17l-5-5"></path></svg> 10 or more characters
                </p>
                <p class="mm-check" :class="{ 'mm-check--ok': containsSpecialChar }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:543:22" data-component-name="Check"><path d="M20 6 9 17l-5-5"></path></svg> 1 or more special characters (*,!,#, etc)
                </p>
                <p class="mm-check" :class="{ 'mm-check--ok': containsNumber }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:543:22" data-component-name="Check"><path d="M20 6 9 17l-5-5"></path></svg> 1 or more numbers
                </p>
                <p class="mm-check" :class="{ 'mm-check--ok': passwordsMatch }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:543:22" data-component-name="Check"><path d="M20 6 9 17l-5-5"></path></svg> Passwords match
                </p>
              </div>

              <button
                type="submit"
                class="mm-btn mm-btn--orange mm-btn--full"
                :disabled="!newPwValid || !containsSpecialChar || !containsNumber || !passwordsMatch || submitting"
              >
                <Loader2 v-if="submitting" :size="16" class="mm-spin" />
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check w-3 h-3" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:543:22" data-component-name="Check"><path d="M20 6 9 17l-5-5"></path></svg>
                <span>Create account</span>
              </button>
            </form>

            <!-- STEP 5 — done -->
            <div v-else-if="step === 'done'" class="mm-done">
              <div class="mm-badge mm-badge--green mm-badge--lg"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-party-popper w-8 h-8 text-[#009118]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:573:20" data-component-name="PartyPopper"><path d="M5.8 11.3 2 22l10.7-3.79"></path><path d="M4 3h.01"></path><path d="M22 8h.01"></path><path d="M15 2h.01"></path><path d="M22 20h.01"></path><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"></path><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17"></path><path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7"></path><path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z"></path></svg></div>
              <div>
                <h1 class="mm-h1">You're all set, {{ firstName.trim() || "there" }}!</h1>
                <p class="mm-sub">
                  Your account for
                  <span class="mm-strong">{{ invite.locationName }}</span> is ready.
                  Jump in and start {{ invite.pathwayName || "your pathway" }}.
                </p>
              </div>
              <div class="mm-summary">
                <p class="mm-summary__row"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:588:22" data-component-name="User"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg> {{ firstName.trim() }} {{ lastName.trim() }}</p>
                <p class="mm-summary__row"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:591:22" data-component-name="Mail"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg> {{ email.trim() }}</p>
                <p class="mm-summary__row"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:594:22" data-component-name="MapPin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg> {{ invite.locationName }}</p>
                <p v-if="invite.stepName" class="mm-summary__row">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-footprints w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:598:24" data-component-name="Footprints"><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path><path d="M16 17h4"></path><path d="M4 13h4"></path></svg> {{ invite.stepName }}
                </p>
              </div>
            </div>
          </div>

          <div class="mm-card__body" v-else  style="background-color: #FFF;">
            <!-- STEP 1 — invitation password -->
						<div class="mm-center">
							<div class="mm-badge"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-key-round w-6 h-6 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/MenteeJoinFlow.tsx:257:22" data-component-name="KeyRound"><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle></svg></div>
							<h1 class="mm-h1">Invalid invitation link!</h1>
							<p class="mm-sub">
								This link either expired or invalid. Please contact administrator.
							</p>
						</div>
					</div>
        </div>

        <p class="mm-join__foot">&copy; 2026 Mentor Mentee · Patent Pending</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import OtpInput from "./OtpInput.vue";
import { notify } from "../../toastStore";

const STEP_ORDER = ["password", "account", "verify", "setPassword", "done"];

export default {
  name: "MenteeJoinFlow",
  components: {
    OtpInput,
  },
  data() {
    return {
			loading: false,
      step: "password",
      // step 1
      invitePw: "",
      showInvitePw: false,
      pwError: false,
      // step 2
      firstName: "",
      lastName: "",
      email: "",
      // step 3
      code: "",
      codeError: false,
      // step 4
      newPw: "",
      confirmPw: "",
      showNewPw: false,
      submitting: false,
			logoSrc: 'https://api.mentormentee.com/web/site_logo/MM-logo.png',
			demoPassword: '5Q22CW',
			demoCode: '123456',
			showHints: false,
			inviteId: '',
			invite: [],
			token: '',
      success_message: '',
      warning_message: '',
      dismissCountDown: 0,
      dismissCountWarningDown: 0,
			autofocus: 0,
    };
  },
  computed: {
    progressSteps() {
      return STEP_ORDER.slice(0, 4);
    },
    stepIndex() {
      return STEP_ORDER.indexOf(this.step);
    },
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
    },
    accountComplete() {
      return (
        !!this.firstName.trim() && !!this.lastName.trim() && this.emailValid
      );
    },
    newPwValid() {
      return this.newPw.length >= 10;
    },
    containsSpecialChar() {
      return /[!@#$%^&*]/.test(this.newPw);
    },
    containsNumber() {
      return /[0-9]/.test(this.newPw);
    },
    passwordsMatch() {
      return this.newPw.length > 0 && this.newPw === this.confirmPw;
    },
  },
  created() {
    this.token = this.$route.params.token;
		this.loading = true;
		axios('/custom/get-account-invitation-token',
		{
			method: 'GET',     
			params: {
				invitationLink: this.token,
			}
		})
		.then(resp => {  
			this.loading = false;      
			this.invite = resp.data;
			this.inviteId = resp.data.id;
		})
		.catch(() => {
		});      

  },
  methods: {
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    countDownWarningChanged(dismissCountWarningDown) {
      this.dismissCountWarningDown = dismissCountWarningDown
    },
    urlInviteId() {
      if (typeof window === "undefined" || !window.location) return "";
      try {
        return new URLSearchParams(window.location.search).get("invite") || "";
      } catch (e) {
        return "";
      }
    },

    submitInvitePassword() {
      if (this.invitePw.trim()) {
				axios('/custom/verify-invitation-password/' + this.inviteId,
				{
					method: 'GET', 
          params: {
            password: this.invitePw,
          }
				})
				.then(response => {     
					if(response.data == 1) {
						this.pwError = false;
						this.step = "account";
					}           
					else {
            this.pwError = true;
            notify({
              title: "Incorrect password",
              description: "Double-check the invitation password and try again.",
              variant: "destructive",
            });
						this.invitePw = '';
					}
				})
				.catch(error => {
				});
      } else if(!this.invitePw) {
				this.pwError = true;
        notify({
          title: "Incorrect password",
          description: "Double-check the invitation password and try again.",
          variant: "destructive",
        });
				this.invitePw = '';
      } else {
				this.pwError = true;
        notify({
          title: "Incorrect password",
          description: "Double-check the invitation password and try again.",
          variant: "destructive",
        });
				this.invitePw = '';
      }
    },
    submitAccount() {
      if (!this.accountComplete) return;
      this.submitting = true;
      // Simulate sending a verification email.
			axios('/custom/verify-invitation-email/' + this.email,
			{
				method: 'POST', 
			})
			.then(response => {     
				if(response.data == 1) {
					this.submitting = false;
					this.step = "verify";
          notify({
            title: "Verification code sent",
            description: `We emailed a 6-digit code to ${this.email.trim()}.`,
          });
				}           
				else {
					this.submitting = false;
          notify({
            title: "Email id already exists.",
            description: `Email Id - ${this.email.trim()} already exists. Please contact administrator.`,
            variant: "destructive",
          });
				}
			})
			.catch(error => {
			});
    },
    resendCode: function() {
      axios('/custom/account-invitation/resend-code/' + this.email,
      {
          method: 'GET', 
      })
      .then(response => {                
        notify({
          title: "Code resent",
          description: `A new code was sent to ${this.email.trim()}.`,
        });
      })
      .catch(error => {
      });

    },
    onCodeInput(v) {
      this.code = v;
      this.codeError = false;
    },
    submitCode(value) {
      const entered = (value != null ? value : this.code).trim();
      axios('/custom/account-invitation-verify-code',
      {
				method: 'POST', 
				data: {
					"code": entered,
					"email": this.email,
				}
      })
      .then(response => {                
				if(response.data == 1) {
					this.codeError = false;
					this.step = "setPassword";
				}
				else {
					this.code = '';
					this.$refs.otpInput.focusBox(0);
          notify({
            title: "Invalid code",
            description: "That code doesn't match. Please try again.",
            variant: "destructive",
          });
				}
      })
      .catch(error => {
      });
    },
    submitPassword() {
			axios('/custom/inviteaccount-mentee-insert',
			{
					method: 'POST', 
					data: {
						"id": this.inviteId,
						"first_name": this.firstName,
						"last_name": this.lastName,
						"email": this.email,
						"password": this.newPw,
					},
			})
			.then(response => {                
        this.submitting = false;
        this.step = "done";
			})
			.catch(error => {
			});
    },
  },
};
</script>

<style scoped>
.mm-join {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #002f42 0%, #013a52 50%, #00131c 100%);
}
.mm-join__banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}
.mm-join__banner-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.mm-join__exit {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: inherit;
  font-size: 12px;
  cursor: pointer;
}
.mm-join__exit:hover {
  color: #fff;
}
.mm-join__center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.mm-join__col {
  width: 100%;
  max-width: 28rem;
}
.mm-join__foot {
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  margin: 16px 0 0;
}

.mm-card {
  background: none;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
.mm-card__head {
  background: #002f42;
  padding: 32px 32px 24px;
  text-align: center;
}
.mm-card__logo {
  height: 48px;
  width: auto;
  object-fit: contain;
  margin: 0 auto 16px;
  display: block;
  filter: brightness(0) invert(1);
}
.mm-card__wordmark {
  color: #fff;
  font-weight: 800;
  letter-spacing: 1px;
  font-size: 20px;
  margin-bottom: 16px;
}
.mm-card__eyebrow {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}
.mm-card__location {
  color: #fff;
  font-weight: 600;
  font-size: 18px;
  margin: 2px 0 0;
}
.mm-card__pathway {
  color: #7fd3f5;
  font-size: 12px;
  margin: 4px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.mm-card__progress {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 20px 32px 0;
}
.mm-bar {
  height: 6px;
  flex: 1;
  border-radius: 9999px;
  background: #e7ebee;
  transition: background-color 0.2s;
}
.mm-bar--on {
  background: #00acee;
}
.mm-card__body {
  padding: 32px;
}

.mm-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.mm-center {
  text-align: center;
}
.mm-badge {
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  background: rgba(0, 172, 238, 0.1);
  color: #00acee;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}
.mm-badge--green {
  background: rgba(0, 145, 24, 0.1);
  color: #009118;
}
.mm-badge--lg {
  width: 64px;
  height: 64px;
}
.mm-h1 {
  font-size: 20px;
  font-weight: 700;
  color: #002f42;
  margin: 0;
}
.mm-sub {
  font-size: 14px;
  color: #6b7785;
  margin: 6px 0 0;
}
.mm-strong {
  color: #002f42;
  font-weight: 500;
}

.mm-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mm-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.mm-label {
  font-size: 14px;
  font-weight: 700;
  color: #1f2a30;
	margin: 0;
	line-height: 15px;
}
.mm-input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2a30;
  background: #fff;
  outline: none;
}
.mm-input:focus {
  border-color: #00acee;
  box-shadow: 0 0 0 3px rgba(0, 172, 238, 0.15);
}
.mm-input--error {
  border-color: #ee4200;
}
.mm-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.mm-row {
  display: flex;
  gap: 8px;
}
.mm-icon-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  background: #fff;
  color: #1f2a30;
  cursor: pointer;
}
.mm-icon-btn:hover {
  background: #f3f5f6;
}
.mm-hint {
  font-size: 11px;
  color: #6b7785;
  margin: 0;
}
.mm-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #002f42;
  font-weight: 600;
}
.mm-error-text {
  font-size: 11px;
  color: #ee4200;
  margin: 0;
}

.mm-otp-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.mm-link {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: none;
  font-size: 12px;
  color: #00acee;
  cursor: pointer;
}
.mm-link:hover {
  text-decoration: underline;
}

.mm-checks {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mm-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7785;
  margin: 0;
}
.mm-check--ok {
  color: #009118;
}

.mm-done {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 0;
}
.mm-summary {
  border: 1px solid #e7ebee;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mm-summary__row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7785;
  margin: 0;
}
.mm-exit-link {
  width: 100%;
  background: none;
  border: none;
  font-size: 12px;
  color: #6b7785;
  cursor: pointer;
}
.mm-exit-link:hover {
  color: #1f2a30;
}

.mm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.mm-btn--full {
  width: 100%;
}
.mm-btn--grow {
  flex: 1;
}
.mm-btn--orange {
  background: #ee4200;
  color: #fff;
}
.mm-btn--orange:hover {
  background: #cc3800;
}
.mm-btn--blue {
  background: #00acee;
  color: #fff;
}
.mm-btn--blue:hover {
  background: #0095d0;
}
.mm-btn--outline {
  background: #fff;
  border-color: #d6dde1;
  color: #1f2a30;
}
.mm-btn--outline:hover {
  background: #f3f5f6;
}
.mm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.mm-spin {
  animation: mm-spin 1s linear infinite;
}
@keyframes mm-spin {
  to {
    transform: rotate(360deg);
  }
}
.bg-gradient-to-br {
	min-height: 100vh;
	padding-top: 50px;
	--tw-gradient-position: to bottom right in oklab;
	background-image: linear-gradient(to bottom right in oklab, #002F42 0%, #013a52 50%, #00131c 100%);
}
.alert {
  margin-bottom: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/create_account/create_user_from_invitation.vue